import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Skeleton,
  Text,
  UnorderedList,
  useOutsideClick,
} from "@chakra-ui/react";
import * as DateFns from "date-fns";
import { useCombobox } from "downshift";
import { useField, useFormikContext } from "formik";
import { FormControl } from "formik-chakra-ui";
import _ from "lodash";
import Markdown from "markdown-to-jsx";
import React from "react";
import { RiCloseLine } from "react-icons/ri";
import { Glossary } from "src/components/Text/Glossary";
import { useSearchApplicants } from "src/generated/hooks/form-service/queries";
import type { ApplicantSummary } from "src/generated/hooks/form-service/requests";
import { useFocus } from "src/hooks/useFocus";

const PAGE_SIZE = 5;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;

interface EligibleStudentSelectInputProps {
  name: string;
  organizationId: string;
  placeholder?: string;
  label?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onSelectedStudent?: (value: ApplicantSummary | null | undefined) => void;
}

function toFullName(
  person: ApplicantSummary | null | undefined,
  defaultValue: string = ""
): string {
  if (!person) return defaultValue;
  const parts = [
    person.first_name,
    person.middle_name,
    person.last_name,
  ].filter(Boolean);
  return parts.join(" ") || defaultValue;
}

export function EligibleStudentSelectInput({
  name,
  organizationId,
  onSelectedStudent,
  placeholder = "Search for student by name or ID",
  label = "Student",
  isDisabled = false,
  isRequired = false,
}: EligibleStudentSelectInputProps) {
  const outsideRef = React.useRef<HTMLDivElement>(null);

  const formik = useFormikContext();
  const [field, { initialValue }, helper] = useField(name);
  const [hasUserSelected, setHasUserSelected] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [applicants, setApplicants] = React.useState<ApplicantSummary[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const [selectedStudent, setSelectedStudent] = React.useState<
    ApplicantSummary | null | undefined
  >(undefined);

  const searchValue = inputValue.trim();

  const [debouncedSearchValue, setDebouncedSearchValue] =
    React.useState(searchValue);
  const debouncedSetSearchValue = React.useMemo(
    () =>
      _.debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => {
    debouncedSetSearchValue(searchValue);
    return () => debouncedSetSearchValue.cancel();
  }, [searchValue, debouncedSetSearchValue]);

  const { data: searchResponse, isLoading } = useSearchApplicants(
    {
      query: {
        organizationId,
        search: debouncedSearchValue,
        limit: PAGE_SIZE,
        offset,
      },
    },
    undefined,
    {
      enabled:
        !!organizationId && debouncedSearchValue.length >= MIN_SEARCH_LENGTH,
    }
  );
  const data = searchResponse?.data;

  useOutsideClick({
    ref: outsideRef,
    handler: () => closeMenu(),
  });

  React.useEffect(() => {
    if (debouncedSearchValue.length < MIN_SEARCH_LENGTH) {
      setApplicants([]);
      setTotal(0);
      setOffset(0);
      return;
    }
    if (!data) return;
    if (offset === 0) {
      setApplicants(data.applicants);
    } else {
      setApplicants((prev) => _.unionBy(prev, data.applicants, "id"));
    }
    setTotal(data.total);
  }, [data, offset, debouncedSearchValue]);

  React.useEffect(() => {
    setOffset(0);
  }, [debouncedSearchValue]);

  React.useEffect(() => {
    if (!hasUserSelected && applicants.length > 0 && initialValue) {
      const preSelected = applicants.find((s) => s.id === initialValue);
      if (preSelected) {
        setSelectedStudent(preSelected);
        onSelectedStudent?.(preSelected);
        setInputValue(toFullName(preSelected));
        setOffset(0);
      }
    }
  }, [applicants, hasUserSelected, initialValue, onSelectedStudent]);

  const fetchMore = React.useCallback(() => {
    if (applicants.length < total && searchValue.length >= MIN_SEARCH_LENGTH) {
      setOffset(applicants.length);
    }
  }, [applicants.length, total, searchValue]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoading && applicants.length > 0) {
          fetchMore();
        }
      },
      { rootMargin: "20px" }
    );
    const current = observerRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [applicants, isLoading, fetchMore]);

  const [isFocused, setIsFocused] = React.useState(false);
  const [inputRef, setInputFocus] = useFocus();
  React.useEffect(() => {
    if (isFocused) setInputFocus();
  }, [setInputFocus, isFocused]);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.onblur = () => setIsFocused(false);
    }
  }, [inputRef, isFocused]);

  const resetField = () => {
    helper.setTouched(true, false);
    helper.setValue("");
    setInputValue("");
    setApplicants([]);
    setSelectedStudent(null);
    onSelectedStudent?.(null);
    setOffset(0);
    setIsFocused(true);
  };

  const handleChange = (value: string | null) => {
    const id = value ?? "";
    if (id === field.value) return;
    setHasUserSelected(true);
    const found = applicants.find((s) => s.id === id) ?? null;
    setSelectedStudent(found);
    onSelectedStudent?.(found);
    helper.setTouched(true, false);
    helper.setValue(id);
    // Defer so Formik has applied setValue before we re-run validation
    setTimeout(() => {
      void formik.validateField(name);
    }, 0);
  };

  const noResults =
    debouncedSearchValue.length >= MIN_SEARCH_LENGTH &&
    !isLoading &&
    data !== undefined &&
    data.total === 0;

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
  } = useCombobox<ApplicantSummary>({
    items: applicants,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    itemToString: toFullName,
    onInputValueChange({ inputValue: newValue }) {
      if (newValue !== inputValue) {
        setInputValue(newValue ?? "");
        setOffset(0);
      }
    },
    stateReducer(state, actionAndChanges) {
      const { changes, type, index } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const newIndex =
            index !== undefined && index >= 0
              ? index
              : changes.highlightedIndex !== undefined &&
                changes.highlightedIndex >= 0
              ? changes.highlightedIndex
              : undefined;
          const newSelectedItem =
            newIndex !== undefined ? applicants[newIndex] : null;
          return { ...changes, selectedItem: newSelectedItem };
        }
        default:
          return changes;
      }
    },
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem) handleChange(selectedItem.id);
    },
  });

  const openMenuUnlessDisabled = React.useCallback(() => {
    if (!isDisabled) openMenu();
  }, [isDisabled, openMenu]);

  const markdownName = React.useCallback(
    (displayName: string) => {
      if (!inputValue) return displayName;
      const escaped = inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "ig");
      return displayName.replace(regex, "**$1**");
    },
    [inputValue]
  );

  const renderItem = (applicant: ApplicantSummary) => {
    const displayName = toFullName(applicant);
    const name = markdownName(displayName);
    return (
      <Flex direction="column" gap={0} width="100%">
        <Flex direction="row" gap={0} width="100%">
          <Text fontSize="md" marginEnd="auto">
            <Markdown>{name}</Markdown>
          </Text>
          {applicant.birth_date && (
            <Text fontSize="sm">
              Born{" "}
              {DateFns.format(
                DateFns.parse(applicant.birth_date, "yyyy-MM-dd", new Date()),
                "MM/dd/yyyy"
              )}
            </Text>
          )}
        </Flex>
        <Text fontSize="sm" overflow="visible">
          ID: {applicant.id}
        </Text>
        {applicant.reference_id && (
          <Text fontSize="sm">Reference ID: {applicant.reference_id}</Text>
        )}
      </Flex>
    );
  };

  const renderLoadingItem = () => (
    <Flex direction="column" gap={0} width="100%">
      <Flex direction="row" gap={0} width="100%">
        <Skeleton height="1.25rem" width="60%" marginEnd="auto" />
        <Skeleton height="1rem" width="30%" />
      </Flex>
      <Skeleton height="1rem" width="40%" marginTop="0.5rem" />
    </Flex>
  );

  return (
    <Box position="relative" width="100%" ref={outsideRef}>
      <FormControl name={name} isRequired={isRequired}>
        <FormLabel {...getLabelProps()} htmlFor={name}>
          <Glossary>{label}</Glossary>
        </FormLabel>
        {selectedStudent ? (
          <Flex
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            paddingLeft={4}
            paddingRight={3}
            paddingTop={2}
            paddingBottom={2}
            gap={2.5}
            direction="row"
          >
            {renderItem(selectedStudent)}
            {!isDisabled && (
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                p="0"
                onClick={resetField}
              >
                <RiCloseLine />
              </Button>
            )}
          </Flex>
        ) : (
          <>
            <InputGroup
              onClick={openMenuUnlessDisabled}
              onFocus={openMenuUnlessDisabled}
              display="flex"
              justifyContent="center"
            >
              <InputLeftElement children={<SearchIcon color="gray.500" />} />
              <Input
                {...getInputProps(
                  {
                    onBlur: () => {
                      if (hasUserSelected || inputValue.length > 0) {
                        helper.setTouched(true);
                      }
                    },
                    ref: inputRef,
                  },
                  { suppressRefError: true }
                )}
                value={inputValue}
                placeholder={placeholder}
                isDisabled={isDisabled}
                required={false}
              />
            </InputGroup>
            <UnorderedList
              {...getMenuProps({}, { suppressRefError: true })}
              display={isOpen ? "block" : "none"}
              position="absolute"
              background="white"
              marginLeft={0}
              zIndex="2000"
              boxShadow="lg"
              width="100%"
              marginTop={1}
              overflow="auto"
              maxHeight="300px"
            >
              {!noResults &&
                !isLoading &&
                applicants.map((applicant, index) => (
                  <ListItem
                    {...getItemProps({ item: applicant, index })}
                    key={applicant.id}
                    backgroundColor={
                      highlightedIndex === index ? "blackAlpha.100" : "white"
                    }
                    listStyleType="none"
                  >
                    <Flex align="center" minHeight="16" padding="4">
                      {renderItem(applicant)}
                    </Flex>
                  </ListItem>
                ))}
              {noResults && (
                <ListItem textAlign="center" p={3}>
                  <Text color="gray.500" fontSize="md">
                    No eligible students found
                  </Text>
                </ListItem>
              )}
              {isLoading && (
                <ListItem textAlign="center" p={2}>
                  <Flex align="center" minHeight="16" padding="4">
                    {renderLoadingItem()}
                  </Flex>
                </ListItem>
              )}
              <Box ref={observerRef} height="5px" />
            </UnorderedList>
          </>
        )}
      </FormControl>
    </Box>
  );
}
